import { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux";
import { ParentScreenCtx } from "/src/pages/workbench_build/WorkbenchBuild";
import { treeActions } from "../../../../redux/slices/treeSlice";
import { SectionHeader } from "../SectionHeader";
import { DefaultDropdown } from "./DefaultDropdown";
import { Autocomplete, Button, MenuItem, Stack, TextField, Tooltip } from "@mui/material";
import { CheckboxesContainer } from "./CheckboxesContainer";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import { debounceFunction } from "../../../../utils/utilFunctions";
import { cloneDeep, debounce, isEqual, set } from "lodash";
import { eachDeep } from "deepdash-es/standalone";
import DatePattern from "../../PatternModal/DatePattern";
import { lgDocPath } from "../../../../constants/languageDocPaths";
import { PatternControl } from "../PatternControl";
import { ConstantValueControl } from "./ConstantValueControl";
import { TimeZone } from "./TimeZone";
import { FieldIdentifier } from "./FieldIdentifier";
import LightTooltip from "../../../../commonComponents/LightTooltip";
import { LoadingEase } from "../../../LoadingEase";

export const ElementFormattingOptionsSection = ({ sourceNodeInfo, updatedSelectedNode, setUpdatedSelectedNode, ftClone, hasField }) => {
  //util
  const { t } = useTranslation();
  const dispatch = useDispatch();

  //redux
  const parentScreen = useContext(ParentScreenCtx);
  const queryParams = useSelector((state) => state.queryParams);
  const { rightTree } = useSelector((state) => state.treeData[parentScreen]);
  const translatedData = rightTree.tree[rightTree.currentTab].translatedData;
  const selectedNode = rightTree.tree[rightTree.currentTab].selectedNode;
  const nodeField = hasField ? updatedSelectedNode.FIELD : updatedSelectedNode;
  const templateHeader = rightTree.tree.templateHeader;

  const [loadingStart, setLoadingStart] = useState(false);
  const [checkboxesLoaded, setCheckboxesLoaded] = useState(false);

  useEffect(() => {
    if (ftClone) handleFieldtypeChange();
  }, [ftClone]);

  //add and delete props that aren't/are used in this FIELDTYPE
  const handleFieldtypeChange = () => {
    //length has different maximums for different fieldtypes, so we need to set the max for the current fieldtype.
    // handleLengthBlur({ target: { value: lengthValue } }, ftClone)
    if (ftClone === "CONSTANT") {
      handleRepeatNumBlur({ target: { value: "1" } }, ftClone)
    } else {
      setConstantValue("");
      modifyUpdatedNode("VALUE", null);
      setLengthValue("0");
      modifyUpdatedNode("LENGTH", "0");
    }
  }

  const modifyUpdatedNode = (prop, val) => {
    if (hasField) {
      if (val !== "" && val !== undefined) {
        setUpdatedSelectedNode(prev => {
          return { ...prev, FIELD: {...prev.FIELD, [prop]: val} }
        });
      } else {
        setUpdatedSelectedNode(prev => {
          return { ...prev, FIELD: {...prev.FIELD, [prop]: null} }
        });
      }
    } else {
      if (val !== "" && val !== undefined) {
        setUpdatedSelectedNode(prev => {
          return { ...prev, [prop]: val }
        });
      } else {
        setUpdatedSelectedNode(prev => {
          return { ...prev, [prop]: null }
        });
      }
    }
  }

  // ***** Conditional controls logic, organized by control *****

  //*** Convert Case - CONVERT_CASE
  const convertCaseLabel = t("LBL_CONVERT_CASE").replace(':', '');
  const convertCaseOptions = [
    <MenuItem key={0} value={'DO_NOT_CONVERT'}>{t('DO_NOT_CONVERT')}</MenuItem>,
    <MenuItem key={1} value={'UPPER_CASE'}>{t('UPPER_CASE')}</MenuItem>,
    <MenuItem key={2} value={'LOWER_CASE'}>{t('LOWER_CASE')}</MenuItem>,
    <MenuItem key={3} value={'PROPER_CASE'}>{t('PROPER_CASE')}</MenuItem>,
  ]
  const convertCaseDefault = nodeField?.CONVERT_CASE ? nodeField.CONVERT_CASE : "DO_NOT_CONVERT";
  const [convertCaseValue, setConvertCaseValue] = useState(convertCaseDefault);
  const handleConvertCaseChange = (val) => {
    setConvertCaseValue(val);

    if (val === "DO_NOT_CONVERT") modifyUpdatedNode("CONVERT_CASE", null);
    else modifyUpdatedNode("CONVERT_CASE", val);
  }

  //*** Inbound Date Pattern - INBOUND_PATTERN*/
  const inboundPatternLabel = t("LBL_INBOUND_DATE_PATTERN").replace(':', '');
  const [ibDatePattern, setIbDatePattern] = useState(nodeField?.INBOUND_PATTERN || "");
  const handleIbDatePatternChange = (val) => {
    setIbDatePattern(val)
    modifyUpdatedNode("INBOUND_PATTERN", val);
  }

  //*** Pattern - PATTERN*/
  const patternLabel = t("PATTERN");
  const [patternValue, setPatternValue] = useState(nodeField?.PATTERN || "");
  const handlePatternChange = (val) => {
    setPatternValue(val);
    modifyUpdatedNode("PATTERN", val);
  }

  //*** Decimal Point Position - PATTERN */ 
  const decPointPrecLabel = t("LBL_DECIMAL_POINT_PRECISION").replace(':', '');
  const [signed, setSigned] = useState(nodeField?.UNSIGNED === "TRUE" ? false : true);
  const handleSignedChange = (val) => {
    setSigned(val);

    if (val) modifyUpdatedNode("UNSIGNED", "");
    else modifyUpdatedNode("UNSIGNED", "TRUE");
  }

  //*** TIMEZONE */
  const [timezoneValue, setTimezoneValue] = useState(nodeField?.TIMEZONE || "");
  const handleTimezoneChange = (val) => {
    setTimezoneValue(val);
    modifyUpdatedNode("TIMEZONE", val);
  }

  //*** Padding Direction - PADDIR
  const paddingDirectionLabel = t("LBL_PADDING_DIRECTION").replace(':', '');
  const paddingDirectionDefault = nodeField?.PADDIR ? nodeField.PADDIR : "RIGHT";
  const [paddingDirectionValue, setPaddingDirectionValue] = useState(paddingDirectionDefault);
  const paddingDirectionOptions = [
    <MenuItem key={0} value={'LEFT'}>{t('LEFT')}</MenuItem>,
    <MenuItem key={1} value={'RIGHT'}>{t('RIGHT')}</MenuItem>,
  ]
  const handlePaddingDirectionChange = (val) => {
    setPaddingDirectionValue(val);
    modifyUpdatedNode("PADDIR", val);
  }

  //*** Constant Value - VALUE
  const constantValueDefault = nodeField?.VALUE|| "";
  let constantValueOptions = [t('CRLF'), t('RECORD_DELIMITER')];
  if (constantValueDefault && !constantValueOptions.includes(constantValueDefault)) constantValueOptions.push(constantValueDefault);
  const [constantValue, setConstantValue] = useState(constantValueDefault);
  const handleConstantValueChange = (val) => {
    setConstantValue(val);
    // html textarea only uses \n when user returns new line.
    // need to insert Carriage Return(%0D \r) before Line Feed (%0A \n) to match SWT functionality.
    // also special case for exact "RECORD DELIMITER" string
    let newVal = val === "RECORD DELIMITER" ? "%25RECORD_DELIMITER%25" : (val.replaceAll("\n", "\r\n"))
    modifyUpdatedNode("VALUE", newVal);

    if (val === "") {
      modifyUpdatedNode("LENGTH", "0");
      setLengthValue("0");
    } else {
      let repeatNum = nodeField.REPEAT ? parseInt(nodeField.REPEAT) : 1;
      //swt counts recdel string as 18 characters. 
      let multNum = val === "RECORD DELIMITER" ? 18 : val.replaceAll("\n", "\r\n").length
      let newLength = (repeatNum * multNum).toString();
      setLengthValue(newLength);
      modifyUpdatedNode("LENGTH", newLength);
    }
  }

  //*** Repeat # times - REPEAT
  const repeatNumLabel = t("LBL_REPEAT_#_TIMES");
  const repeatNumDefault = nodeField?.REPEAT ? nodeField.REPEAT : "1";
  const [repeatNumValue, setRepeatNumValue] = useState(repeatNumDefault);
  const saveRepNumVal = (val) => {
    setRepeatNumValue(val);

    // debouncedSave("REPEAT", val, null, translatedData, nodeField);
  }
  const handleRepeatNumChange = (e) => {
    let newVal = (e?.target?.value || e?.target?.value === "") ? e.target.value : "0";
    if (newVal.length < 6) setRepeatNumValue(newVal);
    let newLength = (parseInt(newVal) * constantValue.length).toString();
    //live update repeat and length states, and clean up / save in blur handler.
    setLengthValue(newLength);
    modifyUpdatedNode("REPEAT", (newVal === "1" || !newVal === "0") ? null : newVal);
    modifyUpdatedNode("LENGTH", newLength);
  }

  const handleRepeatNumBlur = (e, override) => {
    if (override === "CONSTANT" && !constantValue) {
      setRepeatNumValue(1);
      setLengthValue(0);
      modifyUpdatedNode("LENGTH", 0);
      return;
    }
    //strip out '.' and '-' characters because input type="number" allows those. must remove manually
    let strippedVal = e.target.value.replace(/-|\..*/g, '');
    let newVal = "0";

    //limits on repeat number max char
    if (e?.target?.value) {
      if (parseInt(e?.target?.value) < 0) {  //no negatives 
        newVal = "0";
      } else {
        if (strippedVal.length > 5) newVal = strippedVal.substring(0, 5);
        else newVal = strippedVal;
      }
    } else {
      newVal = "0";
    }

    //remove extra leading 0's
    newVal = newVal.replace(/^0+/, '');

    //if user clears input, or input is only multple 0's, set to 0.
    if (e?.target?.value === "" || newVal === "") newVal = "0";

    //below, calc new length based on repeat number and constant value length.
    let valInt = parseInt(newVal);
    if (valInt === 0) valInt = 1;
    let newLength = (valInt * constantValue.length).toString();
    setRepeatNumValue(newVal === "0" ? "1" : newVal);
    setLengthValue(newLength);
    modifyUpdatedNode("REPEAT", newVal === "0" ? "1" : newVal);
    modifyUpdatedNode("LENGTH", newLength);
  }

  //*** Fill Character - FILLCHAR
  const fillCharacterLabel = t("LBL_FILL_CHAR").replace(':', '');
  let fillCharacterDefault = "SPACE";
  if (nodeField?.FILLCHAR) {
    if (nodeField?.FILLCHAR === "%20" || nodeField?.FILLCHAR === " ") fillCharacterDefault = "SPACE";
    else fillCharacterDefault = nodeField.FILLCHAR;
  }
  const fillCharacterOptions = [t('SPACE')];
  const [fillCharacterValue, setFillCharacterValue] = useState(fillCharacterDefault);
  const handleFillCharChangeOuter = (e, val) => {
    //Outer, for when user selects from dropdown
    setFillCharacterValue(val);

    if (val === "SPACE") {
      modifyUpdatedNode("FILLCHAR", " ");
    } else {
      if (val === "" || val === undefined || val === null) {
        modifyUpdatedNode("FILLCHAR", null);
      } else {
        modifyUpdatedNode("FILLCHAR", val);
      }
    }
  }
  const handleFillCharChangeInner = (e) => {
    //triggered from textbox typing etc.
    let val = e?.target?.value || "";

    setFillCharacterValue(val);

    if (val === "SPACE") {
      modifyUpdatedNode("FILLCHAR", " ");
    } else {
      if (val === "" || val === undefined || val === null) {
        modifyUpdatedNode("FILLCHAR", null);
      } else {
        modifyUpdatedNode("FILLCHAR", val);
      }
    }
  }

  //*** Length - LENGTH
  const lengthLabel = t("COL_LENGTH");
  const lengthDefault = nodeField?.LENGTH || "0";
  const [lengthValue, setLengthValue] = useState(lengthDefault);

  const handleLengthChange = (e) => {
    //triggers on input change. allow the user to edit how they want, then clean up and save in blur handler.
    let newLength = (e?.target?.value || e?.target?.value === "") ? e.target.value : "0";
    if (newLength.length < 10) setLengthValue(newLength);
  }

  const handleLengthBlur = (e, override) => {
    //strip out '.' and '-' characters because input type="number" allows those. must remove manually
    let strippedVal = e.target.value.replace(/-|\..*/g, '');
    let newVal = "0";

    if (e?.target?.value) {
      let valInt = parseInt(e?.target?.value);
      if (valInt < 0) {  //no negatives 
        newVal = "0";
      } else if (nodeField.FIELDTYPE === "PACKED_INT" || override === "PACKED_INT") { // limit to only 7 for PACKED_INT
        if (valInt > 7) newVal = "7";
        else newVal = strippedVal;
      } else if (nodeField.FIELDTYPE === "ZONED_INT" || override === "ZONED_INT") { // limit to only for ZONED_INT
        if (valInt > 19) newVal = "19";
        else newVal = strippedVal;
      } else if (override === "CONSTANT") {
        //do nothing for CONSTANT since its value is linked to Repeat # Times control
      } else {
        if (strippedVal.length > 9) newVal = strippedVal.substring(0, 9);
        else newVal = strippedVal;
      }
    } else {
      newVal = "0";
    }

    //remove extra 0's
    newVal = newVal.replace(/^0+/, '');

    //if user clears input, or input is only multple 0's, set to 0.
    if (e?.target?.value === "" || newVal === "") newVal = "0";

    if (override !== "CONSTANT") setLengthValue(newVal);
    if (override !== "CONSTANT") modifyUpdatedNode("LENGTH", newVal);
  }

  //*** Field Identifier - DELIM_FLD_ID filetype
  const fieldIdentifierLabel = t("LBL_FIELD_ID").replace(":", "");
  const [fieldIdentifierValue, setFieldIdentifierValue] = useState(nodeField.FIELD_ID || "");
  const handleFieldIdentifierChange = (val) => {
    setFieldIdentifierValue(val);

    // debouncedSave("FIELD_ID", val, null, translatedData, nodeField);
  }

  //*** Beginning Position - FIXED_LENGTH filetype
  const beginningPositionLabel = t("LBL_BEG_POSITION".replace(":", ""));
  const [beginningPositionValue, setBeginningPositionValue] = useState(0);

  //*** Ending Position - FIXED_LENGTH filetype
  const endingPositionLabel = t("LBL_END_POSITION").replace(":", "");

  //*** Inbound Precision - PRECISION
  const precisionLabel = nodeField?.FIELDTYPE && ["ZONED_INT", "PACKED_INT"].includes(nodeField?.FIELDTYPE) ?
    t("LBL_PRECISION").replace(':', '')
    : t("LBL_PRECISION_IN").replace(':', '')
  const [precisionValue, setPrecisionValue] = useState(nodeField?.PRECISION || "0");
  const handlePrecisionChange = (e) => {
    //triggers on input change. allow the user to edit how they want, then clean up and save in blur handler.
    let newPrec = (e?.target?.value || e?.target?.value === "") ? e.target.value : "0";
    if (newPrec.length < 10) setPrecisionValue(newPrec);
  }

  const handlePrecisionBlur = (e) => {
    let strippedVal = e.target.value.replace(/-|\..*/g, '');
    let newVal = "0";

    if (e?.target?.value) {
      let valInt = parseInt(e?.target?.value);
      if (valInt < 0) {  //no negatives 
        newVal = "0";
      } else {
        if (strippedVal.length > 9) newVal = strippedVal.substring(0, 9);
        else newVal = strippedVal;
      }
    } else {
      newVal = "0";
    }

    //remove extra 0's
    newVal = newVal.replace(/^0+/, '');

    //if user clears input, or input is only multple 0's, set to 0.
    if (e?.target?.value === "" || newVal === "") newVal = "0";

    setPrecisionValue(newVal);
    modifyUpdatedNode("PRECISION", newVal);
  }

  //*** Outbound Precision - PRECISION_OUT
  const precisionOutLabel = t("LBL_PRECISION_OUT").replace(':', '');
  const [precisionOutValue, setPrecisionOutValue] = useState(nodeField?.PRECISION_OUT || "0");

  const handlePrecisionOutChange = (e) => {
    //triggers on input change. allow the user to edit how they want, then clean up and save in blur handler.
    let newPrec = (e?.target?.value || e?.target?.value === "") ? e.target.value : "0";
    if (newPrec.length < 10) setPrecisionOutValue(newPrec);
  }

  const handlePrecisionOutBlur = (e) => {
    let strippedVal = e.target.value.replace(/-|\..*/g, '');
    let newVal = "0";

    if (e?.target?.value) {
      let valInt = parseInt(e?.target?.value);
      if (valInt < 0) {  //no negatives 
        newVal = "0";
      } else {
        if (strippedVal.length > 9) newVal = strippedVal.substring(0, 9);
        else newVal = strippedVal;
      }
    } else {
      newVal = "0";
    }

    //remove extra 0's
    newVal = newVal.replace(/^0+/, '');

    //if user clears input, or input is only multple 0's, set to 0.
    if (e?.target?.value === "" || newVal === "") newVal = "0";

    setPrecisionOutValue(newVal);
    modifyUpdatedNode("PRECISION_OUT", newVal);
  }

  //*** ROUND */
  const [roundValue, setRoundValue] = useState(false);
  const handleRoundChange = (val) => {
    setRoundValue(val);

    // debouncedSave("ROUND", val ? "TRUE" : "", null, translatedData, nodeField);
  }

  //*** On Error / Default Value - KEEP_ORIG_DATA_ON_ERROR / DEFAULT_VALUE
  //if default value selected, enable Default Value input, only use prop DEFAALT_VALUE as val of input. 
  //else if Keep Original Data selected, disable Default Value input, set KEEP_ORIG_DATA_ON_ERROR="TRUE", and remove DEFAULT_VALUE prop
  // On Error dropdown
  const onErrorLabel = t("LBL_ON_ERR").replace(':', '');
  const [onErrorValue, setOnErrorValue] = useState(nodeField?.KEEP_ORIG_DATA_ON_ERROR === "TRUE" ? "KEEP_ORIGINAL_DATA" : "LEAVE_BLANK");
  const onErrorOptions = [
    <MenuItem key={0} value={'KEEP_ORIGINAL_DATA'}>{t('KEEP_ORIGINAL_DATA')}</MenuItem>,
    <MenuItem key={1} value={'LEAVE_BLANK'}>{t('LEAVE_BLANK')}</MenuItem>,
  ]
  const handleOnErrorChange = (val) => {
    setOnErrorValue(val);
    if (val === "KEEP_ORIGINAL_DATA") {
      setDefaultValueDisabled(true);
      modifyUpdatedNode("KEEP_ORIG_DATA_ON_ERROR", "TRUE");
      modifyUpdatedNode("DEFAULT_VALUE", null);
    } else {
      setDefaultValueDisabled(false);
      modifyUpdatedNode("KEEP_ORIG_DATA_ON_ERROR", null);
    }
  }

  // Default Value text input
  const defaultValueLabel = t("LBL_DEFAULT_VALUE").replace(':', '');
  const [defaultValue, setDefaultValue] = useState(nodeField?.DEFAULT_VALUE || "");
  const [defaultValueDisabled, setDefaultValueDisabled] = useState(nodeField?.KEEP_ORIG_DATA_ON_ERROR === "TRUE" ? true : false);
  const handleDefaultValueChange = (e) => {
    if (e?.target?.value || e?.target?.value === "") {
      setDefaultValue(e.target.value);
      modifyUpdatedNode("DEFAULT_VALUE", e.target.value);
    }
  }

  // ***** End of Conditional controls logic *****

  const flagAsLoaded = (val) => {
    if (val) setCheckboxesLoaded(true);
  }

  //used to conditionally render only the controls needed for certain fieldtype
  const controlsShown = nodeField?.FIELDTYPE ? 
    controlOptions[nodeField?.FIELDTYPE] 
    : nodeField?.FIELD?.FIELDTYPE ?
      controlOptions[nodeField.FIELD.FIELDTYPE]
      : [];

  return (<>
    {/* <SectionHeader text={t("LBL_FORMATE_OPTIONS")}/> */}
    <div style={{ width: "100%", display: !loadingStart && checkboxesLoaded ? "flex" : "none", flexWrap: "wrap" }}>
      {/* Constant Value - VALUE */}
      {nodeField?.FIELDTYPE === "CONSTANT" && (
        <div style={{ marginBottom: '10px', width: "100%" }}>
          <ConstantValueControl constantValue={constantValue} setConstantValue={handleConstantValueChange} constantValueOptions={constantValueOptions} />
        </div>
      )}
    </div>

    <div style={{
      flexWrap: "wrap",
      gap: "10px",
      pointerEvents: !loadingStart && checkboxesLoaded ? "auto" : "none",
      display: !loadingStart && checkboxesLoaded ? "flex" : "none"
    }}>
      {/* Convert Case - CONVERT_CASE*/}
      {controlsShown.includes("CONVERT_CASE") && (
        <DefaultDropdown
          value={convertCaseValue}
          setValue={handleConvertCaseChange}
          options={convertCaseOptions}
          label={convertCaseLabel}
          labelId={"fieldConvertCase"}
          selectProps={{ sx: { height: "34px !important" } }}
          formProps={{ sx: { width: "unset", minWidth: "110px", flex: "1" } }}
        />
      )}

      {/* Inbound Date Pattern */}
      {controlsShown.includes("INBOUND_PATTERN") && (
        <PatternControl
          inputId="fieldIbPattern"
          inputLabel={inboundPatternLabel}
          pattern={ibDatePattern}
          setPattern={handleIbDatePatternChange}
          sourceNodeInfo={sourceNodeInfo}
          updatedSelectedNode={nodeField}
          ibDatePatternControl
        />
      )}


      {/* Pattern - TODO - currently works for date, make available for number pattern aswell.*/}
      {(controlsShown.includes("PATTERN") && !controlsShown.includes("DEC_PATTERN")) && (
        <PatternControl
          inputId="fieldPattern"
          inputLabel={patternLabel}
          pattern={(patternValue)}
          setPattern={handlePatternChange}
          noDateChooser={["FLOAT", "INTEGER"].includes(nodeField?.FIELDTYPE)}
          roundValue={roundValue}
          setRoundValue={handleRoundChange}
          precision={precisionValue}
          sourceNodeInfo={sourceNodeInfo}
          setPrecision={(val) => handlePrecisionBlur({ target: { value: val } })}
        />
      )}

      {/* Time Zone */}
      {/* {(controlsShown.includes("TIMEZONE") && templateHeader.FILETYPE !== "XML") && (
        <TimeZone
          id="fieldTimezone"
          inputValue={timezoneValue}
          setInputValue={handleTimezoneChange}
        />
      )} */}

      {/* Padding Direction - PADDIR */}
      {controlsShown.includes("PADDIR") && (
        <DefaultDropdown
          value={paddingDirectionValue}
          setValue={handlePaddingDirectionChange}
          options={paddingDirectionOptions}
          label={paddingDirectionLabel}
          labelId={"fieldPaddingDirection"}
          style={{ minWidth: "115px" }}
          selectProps={{ sx: { height: "34px !important" } }}
          formProps={{ sx: { width: "unset", minWidth: "110px", flex: "1" } }}
        />
      )}

      {/* Fill Character - FILLCHAR*/}
      {controlsShown.includes("FILLCHAR") && (
        <Autocomplete
          id="fieldFillCharacter"
          size="small"
          freeSolo
          options={fillCharacterOptions}
          value={fillCharacterValue}
          onChange={handleFillCharChangeOuter}
          renderInput={(params) => <TextField
            {...params}
            label={t(fillCharacterLabel)}
            value={fillCharacterValue}
            onChange={handleFillCharChangeInner}
            sx={{ height: "34px" }}
            InputProps={{ sx: { height: "34px" } }}
          />}
          sx={{ width: "unset", minWidth: "110px", flex: "1" }}
        />
      )}

      {/* Repeat # times - REPEAT*/}
      {controlsShown.includes("REPEAT") && (
        <TextField
          id="fieldRepeatNum"
          size="small"
          variant="outlined"
          label={repeatNumLabel}
          value={repeatNumValue}
          onChange={handleRepeatNumChange}
          onBlur={handleRepeatNumBlur}
          type="number"
          InputProps={{ sx: { height: "34px !important" } }}
          inputProps={{ min: 0, max: 99999 }}
          sx={{ width: "unset", minWidth: "110px", flex: "1" }}
        />
      )}
      {/* Length - LENGTH / constant*/}
      {controlsShown.includes("LENGTH") && nodeField?.FIELDTYPE === "CONSTANT" ? (
        <LightTooltip title={lengthValue} placement="top">
          <TextField
            id="fieldLength"
            size="small"
            variant="outlined"
            label={lengthLabel}
            value={lengthValue}
            onChange={handleLengthChange}
            onBlur={handleLengthBlur}
            type="number"
            inputProps={{ min: 0, max: 999999999 }}
            InputProps={{ sx: { height: "34px !important" } }}
            disabled
            sx={{ width: "unset", minWidth: "110px", flex: "1", }}
          />
        </LightTooltip>
      ) : (["ZONED_INT", "PACKED_INT"].includes(nodeField.FIELDTYPE) ? (
        <LightTooltip title={nodeField.FIELDTYPE === "PACKED_INT" ? t('BOX_PACKED_INT_MAX_LENGTH_IS_SEVEN') : t('BOX_ZONED_INT_MAX_LENGTH_IS_19')} placement="top">
          <TextField
            id="fieldLength"
            size="small"
            variant="outlined"
            label={lengthLabel}
            value={lengthValue}
            onChange={handleLengthChange}
            onBlur={handleLengthBlur}
            type="number"
            inputProps={{ min: 0, max: nodeField.FIELDTYPE === "PACKED_INT" ? 7 : 19 }}
            InputProps={{ sx: { height: "34px !important" } }}
            sx={{ width: "unset", minWidth: "110px", flex: "1" }}
          />
        </LightTooltip>
      ) : (
        <TextField
          id="fieldLength"
          size="small"
          variant="outlined"
          label={lengthLabel}
          value={lengthValue}
          onChange={handleLengthChange}
          onBlur={handleLengthBlur}
          type="number"
          inputProps={{ min: 0, max: 999999999 }}
          InputProps={{ sx: { height: "34px !important" } }}
          sx={{ width: "unset", minWidth: "110px", flex: "1" }}
        />
      )
      )}

      {/* Inbound Precision - PRECISION*/}
      {controlsShown.includes("PRECISION") && (
        <TextField
          id="fieldPrecision"
          size="small"
          variant="outlined"
          label={precisionLabel}
          value={precisionValue}
          onChange={handlePrecisionChange}
          onBlur={handlePrecisionBlur}
          type="number"
          InputProps={{ sx: { height: "34px !important" } }}
          sx={{ width: "unset", minWidth: "120px", flex: "1" }}
        />
      )}

      {/* Outbound Precision - PRECISION_OUT */}
      {controlsShown.includes("PRECISION_OUT") && (
        <TextField
          id="fieldPrecisionOut"
          size="small"
          variant="outlined"
          label={precisionOutLabel}
          value={precisionOutValue}
          onChange={handlePrecisionOutChange}
          onBlur={handlePrecisionOutBlur}
          type="number"
          InputProps={{ sx: { height: "34px !important" } }}
          sx={{ width: "unset", minWidth: "110px", flex: "1" }}
        />
      )}

      {/* On Error / Default Value - KEEP_ORIG_DATA_ON_ERROR / DEFAULT_VALUE */}
      {controlsShown.includes("KEEP_ORIG_DATA_ON_ERROR") && (<>
        <DefaultDropdown
          value={onErrorValue}
          setValue={handleOnErrorChange}
          options={onErrorOptions}
          label={onErrorLabel}
          labelId={"fieldOnError"}
          formProps={{ sx: { width: "unset", minWidth: "150px", flex: "1" } }}
          selectProps={{ sx: { height: "34px" } }}
        />
        {!defaultValueDisabled && (
          <TextField
            id="fieldDefaultValue"
            size="small"
            variant="outlined"
            label={defaultValueLabel}
            value={defaultValue}
            onChange={handleDefaultValueChange}
            InputProps={{ sx: { height: "34px !important" } }}
            style={{ width: "unset", minWidth: "160px", flex: "1" }}
          />
        )}
      </>)}

      {/* Beginning Position - FIXED_LENGTH FILETYPE*/}
      {templateHeader?.FILETYPE === "FIXED_LENGTH" && (
        <TextField
          id="fieldBeginningPosition"
          size="small"
          variant="outlined"
          label={beginningPositionLabel}
          value={beginningPositionValue}
          type="number"
          sx={{ width: "unset", minWidth: "110px", flex: "1" }}
          InputProps={{ sx: { height: "34px !important" } }}
          disabled={true}
        />
      )}

      {/* Ending Position - FIXED_LENGTH FILETYPE*/}
      {templateHeader?.FILETYPE === "FIXED_LENGTH" && (
        <TextField
          id="fieldEndingPosition"
          size="small"
          variant="outlined"
          label={endingPositionLabel}
          value={(parseInt(beginningPositionValue) + parseInt(lengthValue)) - (parseInt(lengthValue) === 0 ? 0 : 1)}
          type="number"
          sx={{ width: "unset", minWidth: "110px", flex: "1" }}
          InputProps={{ sx: { height: "34px !important" } }}
          disabled={true}
        />
      )}

      {controlsShown.includes("DEC_PATTERN") && (
        <PatternControl
          inputId="fieldDecPattern"
          inputLabel={decPointPrecLabel}
          pattern={patternValue}
          setPattern={handlePatternChange}
          dpp //Decimal Point Position
          signed={signed}
          setSigned={handleSignedChange}
        />
      )}

      {templateHeader?.FILETYPE === "DELIM_FLD_ID" && (
        <FieldIdentifier label={fieldIdentifierLabel} value={fieldIdentifierValue} setValue={handleFieldIdentifierChange} />
      )}
    </div>

    {/* End of conditional inputs */}

    {/* Route all Checkboxes here*/}
    <div style={{
      pointerEvents: loadingStart ? "none" : "auto",
      display: !loadingStart && checkboxesLoaded ? "unset" : "none",
    }}>
      <CheckboxesContainer
        flagAsLoaded={flagAsLoaded}
        updatedSelectedNode={updatedSelectedNode}
        nodeField={nodeField}
        modifyUpdatedNode={modifyUpdatedNode}
        setUpdatedSelectedNode={setUpdatedSelectedNode}
        sourceNodeInfo={sourceNodeInfo}
        ftClone={ftClone}
        isElement={true}
      />
    </div>
  </>)
}

const controlOptions = {
  SELECT: [],
  ALPHA: ["CONVERT_CASE", "PADDIR", "FILLCHAR", "LENGTH"],//
  BIT_FLAG: ["LENGTH", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE"],//
  CONSTANT: ["VALUE", "REPEAT", "LENGTH"],//constant value:VALUE, repeat: REPEAT
  DATE: ["INBOUND_PATTERN", "PATTERN", "TIMEZONE", "PADDIR", "FILLCHAR", "LENGTH", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE"],
  FLOAT: ["PATTERN", "PADDIR", "FILLCHAR", "LENGTH", "PRECISION", "ROUND", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE"],
  INTEGER: ["PATTERN", "PADDIR", "FILLCHAR", "LENGTH", "PRECISION", "ROUND", "PRECISION_OUT", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE"],
  PACKED_INT: ["PATTERN", "DEC_PATTERN", "UNSIGNED", "LENGTH", "PRECISION", "ROUND", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE"],//DEC_PATTERN uses PATTERN prop but is diff label. "ROUND" prop seems to be added TRUE even with no control
  ZONED_INT: ["PATTERN", "DEC_PATTERN", "UNSIGNED", "LENGTH", "PRECISION", "ROUND", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE"],//same as PACKED_INT
  ALL_CONTROLS: [
    "CONVERT_CASE", "INBOUND_PATTERN", "PATTERN", "DEC_PATTERN", "UNSIGNED", "TIMEZONE", "PADDIR", "FILLCHAR",
    "VALUE", "REPEAT", "LENGTH", "PRECISION", "ROUND", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE"
  ]
}

const defaultValues = {
  "CONVERT_CASE": "DO_NOT_CONVERT",
  "INBOUND_PATTERN": "",
  "PATTERN": "",
  "DEC_PATTERN": "",
  "UNSIGNED": false,
  "TIMEZONE": "",
  "PADDIR": "RIGHT",
  "FILLCHAR": " ",
  "VALUE": "",
  "REPEAT": 0,
  "LENGTH": 0,
  "PRECISION": 0,
  "ROUND": "TRUE",
  "KEEP_ORIG_DATA_ON_ERROR": "",
  "DEFAULT_VALUE": ""
}

const styles = {
  popupBtn: {
    minWidth: "32px"
  }
}

//-- util

//find selected field node in transData, calculate beginning position and return number
const getBeginningPosition = (translatedData, nodeField) => {
  const calcLengthTotal = (val, fieldsArr) => {
    let validFieldsLength = 1;
    let nodeFound = false;
    fieldsArr.forEach((o, i) => {

      if (nodeFound) return;
      if (o?.nodeId === val.nodeId) {
        nodeFound = true;
        return;
      } else {
        if (o.LENGTH && o.LENGTH !== "0") {
          validFieldsLength = validFieldsLength + parseInt(o.LENGTH);
        }
      }
    })
    return validFieldsLength;
  }

  const drillData = () => {
    let lengthTotal = 0;
    eachDeep(cloneDeep(translatedData), (value, key, parentArray, ctx) => {
      if (typeof value !== 'object') return;
      if (value?.nodeId && value.nodeId === nodeField.nodeId) {
        lengthTotal = calcLengthTotal(value, parentArray.filter((o) => o?.ICON_DISPLAY === "FIELD"))
      }
    });

    return lengthTotal;
  }

  return drillData();
}
